import { ArrowDownward, ArrowUpward, Download, FilterAlt, SwapVert, Visibility } from "@mui/icons-material";
import { Button, IconButton, MenuItem, TablePagination, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { EpiStatusChip } from "../../components/epi/EpiStatusChip";
import { EPI_COLORS, companyLabel, periodLabel, resultLabels } from "../../components/epi/epiColors";
import { EpiComplianceService } from "../../services/EpiComplianceService";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiInspectionListRowDTO } from "../../types/EpiComplianceDTO";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiPdfService, loadCompanyLogo } from "../../utils/EpiPdfService";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiInspectionList.scss';

dayjs.extend(customParseFormat);

const pageSizes = [25, 50, 100];

type SortField = 'NUMBER' | 'DATE' | 'EMPLOYEE' | 'REGISTRATION' | 'SECTOR' | 'POSITION'
	| 'OK_ITEMS' | 'NON_CONFORM_ITEMS' | 'NOT_APPLICABLE_ITEMS' | 'RESULT';

interface SortColumn {
	field: SortField;
	label: string;
}

const sortColumns: SortColumn[] = [
	{ field: 'NUMBER', label: 'Nº' },
	{ field: 'DATE', label: 'Data' },
	{ field: 'EMPLOYEE', label: 'Colaborador' },
	{ field: 'REGISTRATION', label: 'Matrícula' },
	{ field: 'SECTOR', label: 'Setor' },
	{ field: 'POSITION', label: 'Cargo' },
	{ field: 'OK_ITEMS', label: 'Conformes' },
	{ field: 'NON_CONFORM_ITEMS', label: 'Não conformes' },
	{ field: 'NOT_APPLICABLE_ITEMS', label: 'Não aplicáveis' },
	{ field: 'RESULT', label: 'Resultado' }
];

const EpiInspectionList: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [rows, setRows] = useState<EpiInspectionListRowDTO[]>([]);
	const [appliedPeriod, setAppliedPeriod] = useState('');
	const [search, setSearch] = useState('');
	const [result, setResult] = useState('');
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [fromValue, setFromValue] = useState<Dayjs | null>(null);
	const [toValue, setToValue] = useState<Dayjs | null>(null);
	const [fromError, setFromError] = useState(false);
	const [toError, setToError] = useState(false);
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(false);
	const [sortField, setSortField] = useState<SortField>('DATE');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const service = new EpiComplianceService();
	const configService = new EpiInspectionService();
	const pdfService = new EpiPdfService();
	const requestSeq = useRef(0);
	const lastAppliedFrom = useRef('');
	const lastAppliedTo = useRef('');

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await configService.getCompanies();
			const enabled = (response?.data || []).filter(company => company.enabled);
			setCompanies(enabled);
			if (enabled.length === 1) {
				setCompanyId(enabled[0].companyId);
			}
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_INSPECTION');
		}
	}, []);

	useEffect(() => {
		load();
	}, [companyId]);

	const filtered = filter().sort(compareRows);
	const invalidPeriod = isInvalidPeriod(from, to);
	const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
	const visible = filtered.slice(currentPage * size, currentPage * size + size);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-list">
				<div className="cabecalho">
					<Typography variant="h6">Inspeções realizadas</Typography>
					<span>Consulte as inspeções de EPIs e abra o dossiê de cada uma.</span>
				</div>
				<div className="filtros">
					<TextField
						select
						label="Empresa"
						size="small"
						value={companyId}
						onChange={(e) => setCompanyId(Number(e.target.value))}
					>
						{companies.map(company => (
							<MenuItem key={company.companyId} value={company.companyId}>
								{companyLabel(company)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						label="Colaborador / Matrícula / Nº"
						size="small"
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(0); }}
					/>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Resultado"
						size="small"
						value={result}
						onChange={(e) => { setResult(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos</MenuItem>
						<MenuItem value="CONFORME">Conforme</MenuItem>
						<MenuItem value="NAO_CONFORME">Não conforme</MenuItem>
					</TextField>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<DatePicker
							label="De"
							value={fromValue}
							onChange={(newValue) => handleFromChange(newValue)}
							slotProps={{ textField: { size: 'small', error: fromError, helperText: fromError ? 'Data inválida' : undefined } }}
						/>
						<DatePicker
							label="Até"
							value={toValue}
							onChange={(newValue) => handleToChange(newValue)}
							slotProps={{ textField: { size: 'small', error: toError, helperText: toError ? 'Data inválida' : undefined } }}
						/>
					</LocalizationProvider>
					<div className="acoes-filtro">
						<Button variant="outlined" startIcon={<FilterAlt />} onClick={clearFilters}>
							Limpar filtros
						</Button>
					</div>
				</div>
				{loading
					? getLoading()
					: !filtered.length
						? <div className="sem-execucoes">
							<Typography variant="h6">
								{companyId === ''
									? 'Selecione uma empresa para ver as inspeções.'
									: invalidPeriod
										? 'A data "De" é posterior à data "Até".'
										: `Nenhuma inspeção encontrada ${appliedPeriod || 'no período'}.`}
							</Typography>
						</div>
						: <Fragment>
							<div className="resultados">
								<span>
									Resultados encontrados{appliedPeriod ? ` ${appliedPeriod}` : ''}: {filtered.length}
								</span>
								{permissions.includes('EXPORT_EPI_REPORT') && (
									<Button
										variant="outlined"
										startIcon={<Download />}
										onClick={async () => pdfService.exportInspectionsToPdf(filtered, {
											companyName: companies.find(item => item.companyId === companyId)?.companyName || '',
											period: appliedPeriod,
											ordering: sortDescription()
										}, await loadCompanyLogo(companyId))}
									>
										Exportar relatório
									</Button>
								)}
							</div>
							<div className="tabela">
								<div className="header-tabela">
									{sortColumns.map(column => (
										<p
											key={column.field}
											className="ordenavel"
											title={`Ordenar por ${column.label}`}
											onClick={() => changeSort(column.field)}
										>
											<span>{column.label}</span>
											{sortIcon(column.field)}
										</p>
									))}
									<p>Ação</p>
								</div>
								<div className="body-table">
									{visible.map(row => (
										<div className="linha-empresa" key={row.id}>
											<p>{row.number || '—'}</p>
											<p>{row.inspectionDate || '—'}</p>
											<p title={row.employeeName || '—'}>{row.employeeName || '—'}</p>
											<p title={row.registration || '—'}>{row.registration || '—'}</p>
											<p title={row.sector || '—'}>{row.sector || '—'}</p>
											<p title={row.position || '—'}>{row.position || '—'}</p>
											<p>{row.okItems}</p>
											<p>{row.nonConformItems}</p>
											<p>{row.notApplicableItems}</p>
											<p>
												<EpiStatusChip
													label={resultLabel(row) || '—'}
													color={statusColor(row)}
												/>
											</p>
											<p className="acao">
												<IconButton
													size="small"
													onClick={() => navigate(`/inspecao-epis/inspecoes/${row.id}`)}
												>
													<Visibility fontSize="small" />
												</IconButton>
											</p>
										</div>
									))}
								</div>
							</div>
							<div className="paginacao">
								<TablePagination
									component="div"
									rowsPerPageOptions={pageSizes}
									count={filtered.length}
									rowsPerPage={size}
									page={currentPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
									labelRowsPerPage="Registros por página"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	async function load(overrides?: { from?: string, to?: string }) {
		if (companyId === '') {
			setRows([]);
			return;
		}
		const requestedFrom = overrides?.from ?? from;
		const requestedTo = overrides?.to ?? to;
		const seq = ++requestSeq.current;
		if (isInvalidPeriod(requestedFrom, requestedTo)) {
			setRows([]);
			setAppliedPeriod('');
			setLoading(false);
			return;
		}
		setLoading(true);
		const response = await service.listInspections(Number(companyId), requestedFrom, requestedTo);
		if (seq !== requestSeq.current) {
			return;
		}
		setRows(response?.data?.inspections || []);
		setAppliedPeriod(periodLabel(response?.data?.periodFrom, response?.data?.periodTo));
		setPage(0);
		setLoading(false);
	}

	function clearFilters() {
		setSearch('');
		setResult('');
		const hadPeriod = !!from || !!to;
		setFrom('');
		setTo('');
		setFromValue(null);
		setToValue(null);
		setFromError(false);
		setToError(false);
		lastAppliedFrom.current = '';
		lastAppliedTo.current = '';
		setPage(0);
		if (hadPeriod) {
			load({ from: '', to: '' });
		}
	}

	function handleFromChange(newValue: Dayjs | null) {
		setFromValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setFromError(true);
			return;
		}
		setFromError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedFrom.current) {
			return;
		}
		lastAppliedFrom.current = value;
		setFrom(value);
		load({ from: value });
	}

	function handleToChange(newValue: Dayjs | null) {
		setToValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setToError(true);
			return;
		}
		setToError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedTo.current) {
			return;
		}
		lastAppliedTo.current = value;
		setTo(value);
		load({ to: value });
	}

	function isInvalidPeriod(start: string, end: string) {
		return !!start && !!end && dayjs(start, 'DD/MM/YYYY').isAfter(dayjs(end, 'DD/MM/YYYY'));
	}

	function changeSort(field: SortField) {
		if (field === sortField) {
			setSortDirection(previous => previous === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setPage(0);
	}

	function sortIcon(field: SortField) {
		if (field !== sortField) {
			return <SwapVert className="icone-ordem inativo" fontSize="inherit" />;
		}
		return sortDirection === 'asc'
			? <ArrowUpward className="icone-ordem" fontSize="inherit" />
			: <ArrowDownward className="icone-ordem" fontSize="inherit" />;
	}

	function sortDescription() {
		const column = sortColumns.find(item => item.field === sortField);
		return `${column?.label}, ${sortDirection === 'asc' ? 'crescente' : 'decrescente'}`;
	}

	function compareRows(first: EpiInspectionListRowDTO, second: EpiInspectionListRowDTO) {
		const firstValue = sortValue(first);
		const secondValue = sortValue(second);
		if (firstValue === '' || secondValue === '') {
			return firstValue === secondValue ? 0 : firstValue === '' ? 1 : -1;
		}
		const comparison = typeof firstValue === 'number' && typeof secondValue === 'number'
			? firstValue - secondValue
			: String(firstValue).localeCompare(String(secondValue), 'pt-BR', { numeric: true, sensitivity: 'base' });
		const tieBreak = (Number(first.number) || 0) - (Number(second.number) || 0);
		return (comparison || tieBreak) * (sortDirection === 'asc' ? 1 : -1);
	}

	function sortValue(row: EpiInspectionListRowDTO): string | number {
		switch (sortField) {
			case 'NUMBER':
				return row.number ? Number(row.number) : '';
			case 'DATE':
				return sortableDate(row.inspectionDate);
			case 'EMPLOYEE':
				return row.employeeName || '';
			case 'REGISTRATION':
				return row.registration || '';
			case 'SECTOR':
				return row.sector || '';
			case 'POSITION':
				return row.position || '';
			case 'OK_ITEMS':
				return row.okItems ?? '';
			case 'NON_CONFORM_ITEMS':
				return row.nonConformItems ?? '';
			case 'NOT_APPLICABLE_ITEMS':
				return row.notApplicableItems ?? '';
			default:
				return resultLabel(row);
		}
	}

	function sortableDate(value?: string) {
		const [day, month, year] = (value || '').split('/');
		return year ? Number(`${year}${month}${day}`) : '';
	}

	function resultLabel(row: EpiInspectionListRowDTO) {
		return row.status === 'CANCELADA' ? 'Cancelada' : resultLabels[row.result] || '';
	}

	function statusColor(row: EpiInspectionListRowDTO) {
		if (row.status === 'CANCELADA') {
			return EPI_COLORS.neutral;
		}
		return row.result === 'NAO_CONFORME' ? EPI_COLORS.danger : EPI_COLORS.success;
	}

	function filter() {
		return rows.filter(row => {
			if (result && row.result !== result) {
				return false;
			}
			if (!search) {
				return true;
			}
			const term = search.toUpperCase();
			return (row.employeeName || '').toUpperCase().includes(term)
				|| (row.registration || '').includes(search)
				|| (row.number || '').includes(search);
		});
	}
};

export default EpiInspectionList;
